import { clsx } from 'clsx'
import { compact, forEach, map, trim } from 'lodash'
import { useMemo, type FC } from 'react'
import { graphql, useFragment } from 'react-relay'

import { type ProfileInfo_profile$key } from '@/__generated__/ProfileInfo_profile.graphql'
import { type ProfileInfo_query$key } from '@/__generated__/ProfileInfo_query.graphql'
import { ProfileInfoItem, ProfileInfoMultiItem, ProfileInfoPrompts, Text, VStack, type VStackProps } from '@/components'
import { useHasMetricLocale } from '@/hooks'

export interface ProfileInfoProps extends VStackProps {
  profileRef: ProfileInfo_profile$key | null | undefined
  queryRef: ProfileInfo_query$key | null | undefined
}

export const ProfileInfo: FC<ProfileInfoProps> = ({ className, profileRef, queryRef, ...props }) => {
  const hasMetricLocale = useHasMetricLocale()
  const profileData = useFragment(
    graphql`
      fragment ProfileInfo_profile on Profile {
        activities {
          nodes {
            name
          }
        }
        age
        beliefSystem {
          name
        }
        biography
        childrenPreference {
          name
        }
        closestCity
        deets {
          nodes {
            rowId
            name
            isOther
          }
        }
        educationLevel {
          name
        }
        gender {
          name
        }
        genderOther
        heightCm
        heightFt
        heightIn
        occupation
        politicalView {
          name
        }
        pronoun {
          name
        }
        relationshipInterests {
          nodes {
            name
          }
        }
        school
        userId
        user {
          userDeets {
            nodes {
              deetId
              other
            }
          }
          userPrompts(orderBy: [SEQUENCE_ASC]) {
            nodes {
              answer
              prompt {
                question
              }
            }
          }
        }
        vaccinationStatus {
          name
        }
      }
    `,
    profileRef
  )
  const queryData = useFragment(
    graphql`
      fragment ProfileInfo_query on Query {
        currentUser {
          isMetricSystem
        }
      }
    `,
    queryRef
  )

  const isMetricSystem = queryData?.currentUser?.isMetricSystem ?? hasMetricLocale

  // map of deetId to the user-entered "other" value
  const userDeets = useMemo(() => {
    const deetMap: Record<string, string> = {}

    forEach(profileData?.user?.userDeets?.nodes, (userDeet) => {
      if (userDeet?.deetId && userDeet?.other) {
        deetMap[userDeet.deetId] = userDeet.other
      }
    })

    return deetMap
  }, [profileData?.user?.userDeets?.nodes])

  const promptItems = useMemo(
    () =>
      compact(
        map(profileData?.user?.userPrompts?.nodes, (node) =>
          node?.prompt?.question && node?.answer ? { answer: node.answer, question: node.prompt.question } : null
        )
      ),
    [profileData?.user?.userPrompts?.nodes]
  )

  return (
    <VStack
      className={clsx('w-full gap-4', className)}
      {...props}
    >
      <VStack className='w-full gap-2'>
        <Text
          accessibilityRole='header'
          aria-level={2}
          className='font-poppinsSemiBold color-white'
          size='lg'
          // @ts-ignore ts sometimes doesn't see that this is a react-native-web augment, and complains that it's not real
          tabIndex={0}
        >
          About Me
        </Text>
        <Text
          className='px-2 text-justify font-poppinsRegular color-white'
          // @ts-ignore ts sometimes doesn't see that this is a react-native-web augment, and complains that it's not real
          tabIndex={0}
        >
          {profileData?.biography ? trim(profileData.biography) : ''}
        </Text>
      </VStack>
      <VStack className='w-full gap-2'>
        <Text
          accessibilityRole='header'
          aria-level={2}
          className='font-poppinsSemiBold color-white'
          size='lg'
          // @ts-ignore ts sometimes doesn't see that this is a react-native-web augment, and complains that it's not real
          tabIndex={0}
        >
          Basics
        </Text>
        <VStack className='w-full gap-1 px-2'>
          <ProfileInfoItem
            label='Gender'
            value={profileData?.genderOther ?? profileData?.gender?.name}
          />
          {profileData?.pronoun?.name && (
            <ProfileInfoItem
              label='Pronouns'
              value={profileData.pronoun.name}
            />
          )}
          {profileData?.heightCm ? (
            <ProfileInfoItem
              accessibilityLabel={`Height: ${isMetricSystem ? `${profileData.heightCm} centimeters` : `${profileData.heightFt} feet ${profileData?.heightIn ?? 0} inches`}`}
              label='Height'
              value={
                isMetricSystem
                  ? `${profileData.heightCm}cm`
                  : `${profileData.heightFt}ft ${profileData?.heightIn ?? 0}in`
              }
            />
          ) : null}
          {profileData?.school && (
            <ProfileInfoItem
              label='School'
              value={profileData.school}
            />
          )}
          {profileData?.educationLevel?.name && (
            <ProfileInfoItem
              label='Education'
              value={profileData.educationLevel.name}
            />
          )}
          {profileData?.occupation && (
            <ProfileInfoItem
              label='Occupation'
              value={profileData.occupation}
            />
          )}
          {profileData?.beliefSystem?.name && (
            <ProfileInfoItem
              label='Belief System'
              value={profileData.beliefSystem.name}
            />
          )}
          {profileData?.politicalView?.name && (
            <ProfileInfoItem
              label='Politics'
              value={profileData.politicalView.name}
            />
          )}
          {profileData?.childrenPreference?.name && (
            <ProfileInfoItem
              label='Children Preferences'
              value={profileData.childrenPreference.name}
            />
          )}
          {profileData?.vaccinationStatus?.name && (
            <ProfileInfoItem
              label='Vaccination Status'
              value={profileData.vaccinationStatus.name}
            />
          )}
        </VStack>
      </VStack>
      <ProfileInfoMultiItem
        label='Looking For'
        value={map(profileData?.relationshipInterests?.nodes, (item) => item!.name)}
      />
      <ProfileInfoMultiItem
        label='Activities'
        value={map(profileData?.activities?.nodes, (item) => item!.name)}
      />
      <ProfileInfoMultiItem
        label='Deets'
        value={map(profileData?.deets?.nodes, (item) =>
          item!.isOther && userDeets[item!.rowId] ? `${userDeets[item!.rowId]} (Other)` : item!.name
        )}
      />
      <ProfileInfoPrompts items={promptItems} />
    </VStack>
  )
}
